import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config';
import { 
  Box, 
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
  Grid,
  ListItem,
  ListItemText,
  List,
  TextField,
  Button,
  Switch,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Chip from '@mui/material/Chip';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UpdateIcon from '@mui/icons-material/Update';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import Collapse from '@mui/material/Collapse';
import DeleteIcon from '@mui/icons-material/Delete';

const AdminOrganizationDetails = () => {
  const { org_id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [tabLoading, setTabLoading] = useState({
    details: true,
    orders: false,
    roles: false,
    members: false,
    customers: false,
    jobFlows: false,
    products: false,
    forms: false,
    tags: false,
    sessions: false,
    events: false
  });
  const [orgData, setOrgData] = useState(null);
  const [ordersData, setOrdersData] = useState([]);
  const [rolesData, setRolesData] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedRoles, setEditedRoles] = useState({});
  const [membersData, setMembersData] = useState([]);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [jobFlowsData, setJobFlowsData] = useState([]);
  const [productsData, setProductsData] = useState([]);
  const [formsData, setFormsData] = useState([]);
  const [tagsData, setTagsData] = useState([]);
  const [sessionsData, setSessionsData] = useState([]);
  const [eventsData, setEventsData] = useState([]);

  const [formsDialogOpen, setFormsDialogOpen] = useState(false);
  const [editingFormId, setEditingFormId] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [newQuestionName, setNewQuestionName] = useState('');
  const [newQuestionType, setNewQuestionType] = useState('boolean');
  const [newDropdownOptions, setNewDropdownOptions] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [confirmationName, setConfirmationName] = useState('');
  const [customersData, setCustomersData] = useState([]);
  const [expandedCompany, setExpandedCompany] = useState(false);
  const [expandedCustomer, setExpandedCustomer] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [currentEditData, setCurrentEditData] = useState(null);
  const [editType, setEditType] = useState(''); // 'individual', 'company', 'subcustomer'
  const [isUpdatingTags, setIsUpdatingTags] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editedEvent, setEditedEvent] = useState({});
  const [expandedDepartments, setExpandedDepartments] = useState([]);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [categoryItems, setCategoryItems] = useState([]);


  const handleEditForm = (formId) => {
    setEditingFormId(formId);
    setFormFields(formsData.find(form => form.form_id === formId).form_definition.fields);
    setFormsDialogOpen(true);
  };

  const handleCreateForm = () => {
    setEditingFormId(null);
    setFormsDialogOpen(true);
  };

  const handleFormSubmit = () => {
    // Handle form submission logic here
    setFormsDialogOpen(false);
  };

  const handleAddQuestion = () => {
    if (!newQuestionName) return;
    
    const newField = {
      label: newQuestionName,
      type: newQuestionType,
      options: newQuestionType === 'dropdown' ? newDropdownOptions.split(',').map(o => o.trim()) : undefined
    };
    
    setFormFields([...formFields, newField]);
    setNewQuestionName('');
    setNewQuestionType('boolean');
    setNewDropdownOptions('');
  };

  const handleDeleteQuestion = (index) => {
    setFormFields(formFields.filter((_, i) => i !== index));
  };

  const handleDeleteForm = (form) => {
    setFormToDelete(form);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
        navigate('/');
        return;
    }

    if (confirmationName === formToDelete.form_name) {
      try {
        // Save form to server
        await axios.delete(
            `${API_BASE_URL}/admin/org/${org_id}/delete-form`,
            {
                data: {
                    form_id: formToDelete.form_id
                }
            },
            { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
          if (error.response?.status === 401) {
              navigate('/');
          }
          console.error('Error:', error);
      }
    }
  };

  const handleSaveForm = async() => {
    if (!editingFormId || !formFields.length) return;

    // post to server
    const token = localStorage.getItem('token');
    if (!token) {
        navigate('/');
        return;
    }

    try {
        // Save form to server
        await axios.post(
            `${API_BASE_URL}/admin/org/${org_id}/update-form`,
            {
                form_id: editingFormId,
                form_definition: {
                    fields: formFields
                }
            },
            { headers: { Authorization: `Bearer ${token}` } }
        );
    } catch (error) {
        if (error.response?.status === 401) {
            navigate('/');
        }
        console.error('Error:', error);
    }

    setFormsDialogOpen(false);
    // reload forms data
    const formsResponse = await axios.get(
        `${API_BASE_URL}/admin/org/${org_id}/forms`,
        { headers: { Authorization: `Bearer ${token}` } }
    );
    setFormsData(formsResponse.data);
  };

  const handleEditCustomer = (customer) => {
    setEditType('individual');
    setCurrentEditData({
      id: customer.customer_id,
      name: customer.customer_name,
      email: customer.customer_email,
      phone: customer.customer_number
    });
    setEditDialogOpen(true);
  };

  const handleEditCompany = (company) => {
    setEditType('company');
    setCurrentEditData({
      id: company.company_id,
      name: company.company_name,
      email: company.company_email,
      phone: company.company_number,
      website: company.company_website
    });
    setEditDialogOpen(true);
  };

  const handleEditSubCustomer = (subCustomer) => {
    setEditType('subcustomer');
    setCurrentEditData({
      id: subCustomer.customer_id,
      name: subCustomer.customer_name,
      email: subCustomer.customer_email,
      phone: subCustomer.customer_number
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
        navigate('/');
        return;
    }

    const type = editType === 'company' ? 'company' : 'customer';

    try {
        const response = await axios.post(
            `${API_BASE_URL}/admin/org/${org_id}/update-${type}`,
            {
                ...currentEditData
            },
            { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.status === 200) {
            const customersResponse = await axios.get(
              `${API_BASE_URL}/admin/org/${org_id}/customers`,
              { headers: { Authorization: `Bearer ${token}` } }
          );
          setCustomersData(customersResponse.data);
        }
    } catch (error) {
        if (error.response?.status === 401) {
            navigate('/');
        }
        console.error('Error:', error);
    }
    setEditDialogOpen(false);
  }

  const handleTagChange = (tagIndex, value) => {
    const newTagsData = tagsData.map(tag => 
      tag.tag_index === tagIndex ? {...tag, tag_name: value} : tag
    );
    setTagsData(newTagsData);
  };

  const handleUpdateTags = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
        navigate('/');
        return;
    }

    if (isUpdatingTags) return; // prevent multiple clicks
    setIsUpdatingTags(true);
    try {
      const response = await axios.post(
            `${API_BASE_URL}/admin/org/${org_id}/tags`,
            { tags: tagsData },
            { headers: { Authorization: `Bearer ${token}` } }
        );
    } catch (error) {
      console.error('Error updating tags:', error);
    } finally {
      setIsUpdatingTags(false);
      const tagsResponse = await axios.get(
          `${API_BASE_URL}/admin/org/${org_id}/tags`,
          { headers: { Authorization: `Bearer ${token}` } }
      );
      setTagsData(tagsResponse.data);
    }
  };

  const handleKillSession = async (sessionId) => {
    const token = localStorage.getItem('token');
    if (!token) {
        navigate('/');
        return;
    }
    try {
        const sessionsResponse = await axios.delete(
            `${API_BASE_URL}/admin/org/${org_id}/sessions/${sessionId}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchSessions();
    } catch (error) {
        console.error('Error fetching sessions:', error);
    }
  };
  
  const handleEditEvent = (event) => {
    setEditingEvent(event.id);
    setEditedEvent({
      title: event.title,
      type: event.type,
      start_datetime: new Date(event.start_datetime).toISOString().slice(0, 16),
      end_datetime: new Date(event.end_datetime).toISOString().slice(0, 16),
      location: event.location,
      description: event.description
    });
  };

  const handleFieldChange = (field, value) => {
    setEditedEvent(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdateEvent = async (eventId) => {
    const token = localStorage.getItem('token');
    if (!token) {
        navigate('/');
        return;
    }

    if (isUpdatingTags) return; // prevent multiple clicks
    setIsUpdatingTags(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/admin/org/${org_id}/event/${eventId}`,
        { ...editedEvent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        setEditingEvent('');
        const eventsResponse = await axios.get(
          `${API_BASE_URL}/admin/org/${org_id}/events`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEventsData(eventsResponse.data);
      }
  } catch (error) {
      console.error('Error updating tags:', error);
    } finally {
      setIsUpdatingTags(false);
      const tagsResponse = await axios.get(
          `${API_BASE_URL}/admin/org/${org_id}/events`,
          { headers: { Authorization: `Bearer ${token}` } }
      );
      setEventsData(tagsResponse.data);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    const token = localStorage.getItem('token');
    if (!token) {
        navigate('/');
        return;
    }

   try {
        const response = await axios.delete(
            `${API_BASE_URL}/admin/org/${org_id}/event/${eventId}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.status === 200) {
            const eventsResponse = await axios.get(
              `${API_BASE_URL}/admin/org/${org_id}/events`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setEventsData(eventsResponse.data);
        }
    } catch (error) {
        console.error('Error deleting event:', error);
    }

  };

  const fetchSessions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
        navigate('/');
        return;
    }
    try {
        const sessionsResponse = await axios.get(
            `${API_BASE_URL}/admin/org/${org_id}/sessions`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        setSessionsData(sessionsResponse.data);
    } catch (error) {
        console.error('Error fetching sessions:', error);
    }
  };


  useEffect(() => {
    const fetchOrgDetails = async () => {
      
      const token = localStorage.getItem('token');

      if (!token) {
        navigate('/');
        return;
      }

      try {
        // Main org data
        const response = await axios.get(
          `${API_BASE_URL}/admin/org/${org_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOrgData(response.data);
        setTabLoading(prev => ({...prev, details: false}));

      } catch (error) {
        if (error.response?.status === 401) {
          navigate('/');
        }
        console.error('Error:', error);
      }
    };

    fetchOrgDetails();
  }, [org_id, navigate]);

  const handleTabChange = async (event, newValue) => {

    const token = localStorage.getItem('token');
    if (!token) {
        navigate('/');
        return;
    }

    setActiveTab(newValue);

    const tabNames = ['details', 'orders', 'members', 'customers', 'jobFlows', 'products', 'forms', 'tags', 'sessions', 'events'];
    setTabLoading(prev => ({
      ...prev, 
      [tabNames[newValue]]: true
    }));

    try {
        if (newValue === 0) {
        // Fetch details data
        const response = await axios.get(
            `${API_BASE_URL}/admin/org/${org_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        setOrgData(response.data);
        } else if (newValue === 1) {
          // Fetch orders data
          const ordersResponse = await axios.get(
              `${API_BASE_URL}/admin/org/${org_id}/orders`,
              { headers: { Authorization: `Bearer ${token}` } }
          );
          setOrdersData(ordersResponse.data);
        } else if (newValue === 2) {
          // Fetch roles data
          const rolesResponse = await axios.get(
              `${API_BASE_URL}/admin/org/${org_id}/roles`,
              { headers: { Authorization: `Bearer ${token}` } }
          );
          setRolesData(rolesResponse.data);
        } else if (newValue === 3) {
          // Fetch members data
          const membersResponse = await axios.get(
              `${API_BASE_URL}/admin/org/${org_id}/members`,
              { headers: { Authorization: `Bearer ${token}` } }
          );
          setMembersData(membersResponse.data);
        } else if (newValue === 4) {
          // Fetch customers data
          const customersResponse = await axios.get(
              `${API_BASE_URL}/admin/org/${org_id}/customers`,
              { headers: { Authorization: `Bearer ${token}` } }
          );
          setCustomersData(customersResponse.data);
        } else if (newValue === 5) {
          // Fetch jobFlows data
          const jobFlowsResponse = await axios.get(
              `${API_BASE_URL}/admin/org/${org_id}/jobFlows`,
              { headers: { Authorization: `Bearer ${token}` } }
          );
          setJobFlowsData(jobFlowsResponse.data);
        } else if (newValue === 6) {
          // Fetch products data
          const productsResponse = await axios.get(
              `${API_BASE_URL}/admin/org/${org_id}/products`,
              { headers: { Authorization: `Bearer ${token}` } }
          );
          setProductsData(productsResponse.data);
        } else if (newValue === 7) {
          // Fetch forms data
          const formsResponse = await axios.get(
              `${API_BASE_URL}/admin/org/${org_id}/forms`,
              { headers: { Authorization: `Bearer ${token}` } }
          );
          setFormsData(formsResponse.data);
        } else if (newValue === 8) {
          // Fetch tags data
          const tagsResponse = await axios.get(
              `${API_BASE_URL}/admin/org/${org_id}/tags`,
              { headers: { Authorization: `Bearer ${token}` } }
          );
          setTagsData(tagsResponse.data);
        } else if (newValue === 9) {
          // Fetch sessions data
          const sessionsResponse = await axios.get(
              `${API_BASE_URL}/admin/org/${org_id}/sessions`,
              { headers: { Authorization: `Bearer ${token}` } }
          );
          setSessionsData(sessionsResponse.data);
        } else if (newValue === 10) {
          // Fetch events data
          const eventsResponse = await axios.get(
              `${API_BASE_URL}/admin/org/${org_id}/events`,
              { headers: { Authorization: `Bearer ${token}` } }
          );
          setEventsData(eventsResponse.data);
        }
        setTabLoading(prev => ({
          ...prev, 
          [tabNames[newValue]]: false
        }));
    } catch (error) {
        if (error.response?.status === 401) {
          navigate('/');
        }
        console.error('Error:', error);
    }
  };

  const handleSave = async(roleId) => {
    const token = localStorage.getItem('token');
    if (!token) {
        navigate('/');
        return;
    }

    const role = editedRoles[roleId];

    try {
      const response = await axios.post(`${API_BASE_URL}/admin/org/${org_id}/roles`, role, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      if (response.status !== 200) {
        // pop up error message
        alert('Error updating org role');
        return;
      }

      window.location.reload();
    } catch (error) {
      console.error('Error updating org role:', error);
      throw error;
    }
  };

  const handleResetPassword = (member) => {
    setSelectedMember(member);
    setOpenPasswordDialog(true);
  };

  const handlePasswordSubmit = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/admin/org/${org_id}/members/${selectedMember.id}/reset-password`, {
        password: newPassword
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      if (response.status !== 200) {
        alert('Error resetting password');
        return;
      }
      window.location.reload();
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
    
    setOpenPasswordDialog(false);
    setNewPassword('');
  };

  const handleEditDepartment = (department) => {
    console.log('Editing department');
    setEditingDepartment(prev => ({
      ...prev,
      [department.id]: department
    }));
  };

  const handleDeleteDepartment = (departmentId) => {  
    console.log('Deleting department');
  };

  const toggleExpandDepartment = async (departmentId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/org/${org_id}/departments/${departmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProductCategories(response.data);

      if (response.status !== 200) {
        alert('Error resetting password');
        return;
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }

   if (!expandedDepartments.includes(departmentId)) {
    setExpandedDepartments(departmentId);
    } else {
      setExpandedDepartments([]);
    }
  };

  const handleToggleCategory = async (categoryId) => {

    if (!expandedCategories.includes(categoryId)) {
      setExpandedCategories(categoryId);

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      // fetch category items
      try {
        const response = await axios.get(
          `${API_BASE_URL}/admin/org/${org_id}/categories/${categoryId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setCategoryItems(response.data);

        if (response.status !== 200) {
          alert('Error fetching category items');
          return;
        }
      } catch (error) {
        console.error('Error fetching category items:', error);
        throw error;
      }
    } else {
      setExpandedCategories([]);
    }
  };

  const handleEditCategory = (category) => {
    console.log('Editing category');
  };

  const handleDeleteCategory = (categoryId) => {
    console.log('Deleting category');
  };

  const handleEditItem = (item) => {
    console.log('Editing item:');
  };

  const handleDeleteItem = (itemId) => {
    console.log('Deleting item');
  };


  return (
    <Box>
      <Paper sx={{ mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Details" />
          <Tab label="Orders" />
          <Tab label="Roles" />
          <Tab label="Members" />
          <Tab label="Customers" />
          <Tab label="Job Flows" />
          <Tab label="Products" />
          <Tab label="Forms" />
          <Tab label="Tags" />
          <Tab label="Sessions" />
          <Tab label="Events" />
        </Tabs>
      </Paper>
      {/* Tab 1: Details */}
      {activeTab === 0 && (
        <Box>
          {tabLoading.details ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            
            <Box sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                padding: 2,
                height: 'calc(100vh - 128px)'
            }}>
                {/* Header Box */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    mb: 4,
                    p: 3,
                    backgroundColor: 'background.paper',
                    borderRadius: 1,
                    boxShadow: 1
                }}>
                    {orgData.logo_base64 && (
                        <img 
                            src={orgData.logo_base64}
                            alt="Company Logo"
                            style={{ 
                                width: 120, 
                                height: 120, 
                                objectFit: 'contain',
                                marginRight: 24
                            }}
                        />
                    )}
                    <Box>
                    <Typography variant="h4">{orgData.org_name}</Typography>
                    <Typography variant="body1">Email: {orgData.org_email}</Typography>
                    <Typography variant="body1">Phone: {orgData.org_number}</Typography>
                    </Box>
                </Box>

                {/* Data Boxes */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ 
                            p: 3,
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: 1,
                            textAlign: 'center'
                        }}>
                            <Typography variant="h6">Orders</Typography>
                            <Typography variant="h3">{orgData.order_count}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ 
                            p: 3,
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: 1,
                            textAlign: 'center'
                        }}>
                            <Typography variant="h6">Jobs</Typography>
                            <Typography variant="h3">{orgData.job_count}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ 
                            p: 3,
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: 1,
                            textAlign: 'center'
                        }}>
                            <Typography variant="h6">Roles</Typography>
                            <Typography variant="h3">{orgData.role_count}</Typography>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ 
                            p: 3,
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: 1,
                            textAlign: 'center'
                        }}>
                            <Typography variant="h6">Members</Typography>
                            <Typography variant="h3">{orgData.member_count}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ 
                            p: 3,
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: 1,
                            textAlign: 'center'
                        }}>
                            <Typography variant="h6">Customers</Typography>
                            <Typography variant="h3">{orgData.customer_count}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ 
                            p: 3,
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: 1,
                            textAlign: 'center'
                        }}>
                            <Typography variant="h6">Job Flow</Typography>
                            <Typography variant="h3">{orgData.job_flow_count}</Typography>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ 
                            p: 3,
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: 1,
                            textAlign: 'center'
                        }}>
                            <Typography variant="h6">Product Departments</Typography>
                            <Typography variant="h3">{orgData.product_department_count}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ 
                            p: 3,
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: 1,
                            textAlign: 'center'
                        }}>
                            <Typography variant="h6">Product Categories</Typography>
                            <Typography variant="h3">{orgData.product_category_count}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ 
                            p: 3,
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: 1,
                            textAlign: 'center'
                        }}>
                            <Typography variant="h6">Product Items</Typography>
                            <Typography variant="h3">{orgData.product_item_count}</Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
          )}
        </Box>
      )}
      {/* Tab 1: Orders */}
      {activeTab === 1 && (
        <Box sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            padding: 2,
            height: 'calc(100vh - 128px)',
            backgroundColor: '#f3f3f3ff'
        }}>
          {tabLoading.orders ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
           <Box>
              {ordersData.map((order) => (
                <Paper key={order.order_id} sx={{ p: 3, mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                    <Typography variant="h6">
                      {order.prefix}{order.order_name} - {order.company?.company_name || order.customer?.customer_name}
                    </Typography>
                  {order.tags.map((tag) => (
                    <Chip
                      key={tag.tag_index}
                      label={tag.tag_name}
                      size="small"
                      variant={order[tag.tag_index] ? 'filled' : 'outlined'}
                      sx={{
                        borderColor: '#e0e0e0',
                        backgroundColor: order[tag.tag_index] ? '#4caf50' : 'transparent',
                        color: order[tag.tag_index] ? '#fff' : 'inherit',
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                  ))}
                  <Typography variant="body2" sx={{ 
                    color: '#757575',
                    ml: 'auto',
                    minWidth: '140px',
                    textAlign: 'right'
                  }}>
                    Created at:
                    {new Date(order.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
                <Box sx={{ p: 2, mb: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>

                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2"><strong>Order ID:</strong> {order.order_id}</Typography>
                    <Typography variant="body2"><strong>Quote #:</strong> {order.quote_number || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2"><strong>Invoice #:</strong> {order.invoice_number || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>PO #:</strong> {order.purchase_order_number || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2"><strong>Folder:</strong> {order.folder_path || 'N/A'}</Typography>
                  </Grid>
                </Grid>
                {order.order_description && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2"><strong>Description:</strong> {order.order_description}</Typography>
                  </Box>
                )}
                </Box>
                <Box sx={{ p: 2, mb: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                  {order.customer && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" />
                      <Typography variant="body2">
                        <strong>{order.customer.customer_name}</strong> | 
                        {order.customer.customer_email} | 
                        {order.customer.customer_number}
                      </Typography>
                    </Box>
                  )}
                  
                  {order.company && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" />
                      <Typography variant="body2">
                        <strong>{order.company.company_name}</strong> | 
                        {order.company.company_email} | 
                        {order.company.company_number}
                      </Typography>
                    </Box>
                  )}
                  
                  {(order.street_address || order.suburb || order.state || order.postal_code) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon fontSize="small" />
                      <Typography variant="body2">
                        {order.street_address}, {order.suburb} {order.state}, {order.postal_code}
                      </Typography>
                    </Box>
                  )}
                </Box>
                  
                {order.jobs.map((job) => (
                  <Box key={job.job_id} sx={{
                    mb: 2,
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#f9f9f9'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: 2 }}>
                        {job.job_name}
                      </Typography>
                      <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      backgroundColor: job.step_color || '#e0e0e0',
                      color: '#fff',
                      width: 'fit-content',
                      ml: 2
                    }}>
                      <Typography variant="body2">
                        {job.step_name || 'No Step'}
                      </Typography>
                    </Box>
                      <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        <Chip
                          label="Urgent"
                          size="small"
                          variant={job.urgent ? 'filled' : 'outlined'}
                          sx={{
                            backgroundColor: job.urgent ? '#ff9800' : 'transparent',
                            color: job.urgent ? '#fff' : '#424242',
                            borderColor: '#bdbdbd'
                          }}
                        />
                        <Chip
                          label="Completed"
                          size="small"
                          variant={job.completed ? 'filled' : 'outlined'}
                          sx={{
                            backgroundColor: job.completed ? '#4caf50' : 'transparent',
                            color: job.completed ? '#fff' : '#424242',
                            borderColor: '#bdbdbd'
                          }}
                        />
                        <Chip
                          label="Do Not Proceed"
                          size="small"
                          variant={job.do_not_proceed ? 'filled' : 'outlined'}
                          sx={{
                            backgroundColor: job.do_not_proceed ? '#f44336' : 'transparent',
                            color: job.do_not_proceed ? '#fff' : '#424242',
                            borderColor: '#bdbdbd'
                          }}
                        />
                        <Chip
                          label="Deleted"
                          size="small"
                          variant={job.deleted ? 'filled' : 'outlined'}
                          sx={{
                            backgroundColor: job.deleted ? '#9e9e9e' : 'transparent',
                            color: job.deleted ? '#fff' : '#424242',
                            borderColor: '#bdbdbd'
                          }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#424242' }}>
                      <strong>ID:</strong> {job.job_id}
                      </Typography>
                    <Typography variant="body2" sx={{ color: '#424242' }}>
                      <strong>Description:</strong> {job.job_description}
                    </Typography>
                  </Box>
                ))}
              </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}
      {activeTab === 2 && (
        <Box sx={{ height: 'calc(100vh - 128px)', overflowY: 'auto', pr: 2, pl: 2, backgroundColor: 'rgba(235, 235, 235, 1)' }}>
          {tabLoading.details ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {rolesData?.map((role) => (
                <Paper key={role.role_id} sx={{ p: 3, mb: 2, borderRadius: 2, boxShadow: 1 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 3,
                    pb: 2,
                    borderBottom: '1px solid #eee'
                  }}>
                    {isEditing[role.role_id] ? (
                      <TextField 
                        value={editedRoles[role.role_id]?.role_name || role.role_name} 
                        onChange={(e) => setEditedRoles({
                          ...editedRoles,
                          [role.role_id]: {
                            ...editedRoles[role.role_id],
                            role_name: e.target.value
                          }
                        })}
                        size="small"
                        variant="outlined"
                        multiline
                        rows={1}
                        sx={{ flexGrow: 1, mr: 2 }}
                      />
                    ) : (
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>{role.role_name}</Typography>
                    )}
                    
                    {isEditing[role.role_id] ? (
                      <Button 
                        variant="contained" 
                        color="primary"
                        startIcon={<SaveIcon />} 
                        onClick={() => handleSave(role.role_id)}
                        sx={{ minWidth: 100 }}
                      >
                        Save
                      </Button>
                    ) : (
                      <Button 
                        variant="outlined" 
                        color="primary"
                        startIcon={<EditIcon />} 
                        onClick={() => {
                          setIsEditing({
                            ...isEditing,
                            [role.role_id]: true
                          });
                          setEditedRoles({
                            ...editedRoles,
                            [role.role_id]: {
                              ...role,
                              permissions: role.permissions
                            }
                          });
                        }}
                        sx={{ minWidth: 100 }}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>

                  <Box sx={{ 
                    mb: 2,
                    display: 'flex',
                    gap: 3,
                    alignItems: 'center',
                    color: 'text.secondary'
                  }}>
                    <Typography variant="body2" sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      backgroundColor: 'grey.100'
                    }}>
                      <strong>ID:</strong> {role.role_id}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <CalendarTodayIcon fontSize="small" />
                      {new Date(role.created_at).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <UpdateIcon fontSize="small" />
                      {new Date(role.updated_at).toLocaleDateString()}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Permissions
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {/* Permissions Row */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {isEditing[role.role_id] ? (
                            <>
                              <Chip 
                                label="Dashboard"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      dashboard: prev[role.role_id]?.dashboard === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.dashboard === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.dashboard === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Create Order"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      create_order: prev[role.role_id]?.create_order === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.create_order === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.create_order === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <Chip
                                label="Dashboard"
                                variant="outlined"
                                sx={{
                                  borderColor: role.dashboard === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.dashboard === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Create Order"
                                variant="outlined"
                                sx={{
                                  borderColor: role.create_order === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.create_order === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                            </>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} >
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {isEditing[role.role_id] ? (
                            <>
                              <Chip 
                                label="Current Order Read"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      current_order_read: prev[role.role_id]?.current_order_read === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.current_order_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.current_order_read === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Current Order Update"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      current_order_update: prev[role.role_id]?.current_order_update === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.current_order_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.current_order_update === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Current Order Delete"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      current_order_delete: prev[role.role_id]?.current_order_delete === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.current_order_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.current_order_delete === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <Chip
                                label="Current Order Read"
                                variant="outlined"
                                sx={{
                                  borderColor: role.current_order_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.current_order_read === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Current Order Update"
                                variant="outlined"
                                sx={{
                                  borderColor: role.current_order_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.current_order_update === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Current Order Delete"
                                variant="outlined"
                                sx={{
                                  borderColor: role.current_order_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.current_order_delete === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                            </>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} alignItems="center">
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {isEditing[role.role_id] ? (
                            <>
                              <Chip 
                                label="Finished Order Read"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      finished_order_read: prev[role.role_id]?.finished_order_read === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.finished_order_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.finished_order_read === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Finished Order Update"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      finished_order_update: prev[role.role_id]?.finished_order_update === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.finished_order_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.finished_order_update === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Finished Order Delete"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      finished_order_delete: prev[role.role_id]?.finished_order_delete === 1 ? 0 : 1
                                    }
                                  }));
                                }} 
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.finished_order_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.finished_order_delete === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <Chip
                                label="Finished Order Read"
                                variant="outlined"
                                sx={{
                                  borderColor: role.finished_order_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.finished_order_read === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Finished Order Update"
                                variant="outlined"
                                sx={{
                                  borderColor: role.finished_order_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.finished_order_update === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Finished Order Delete"
                                variant="outlined"
                                sx={{
                                  borderColor: role.finished_order_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.finished_order_delete === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                            </>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} alignItems="center">
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {isEditing[role.role_id] ? (
                            <>
                              <Chip 
                                label="Do Not Proceed Read"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      do_not_proceed_read: prev[role.role_id]?.do_not_proceed_read === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.do_not_proceed_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.do_not_proceed_read === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Do Not Proceed Update"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      do_not_proceed_update: prev[role.role_id]?.do_not_proceed_update === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.do_not_proceed_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.do_not_proceed_update === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Do Not Proceed Delete"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      do_not_proceed_delete: prev[role.role_id]?.do_not_proceed_delete === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.do_not_proceed_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.do_not_proceed_delete === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <Chip
                                label="Do Not Proceed Read"
                                variant="outlined"
                                sx={{
                                  borderColor: role.do_not_proceed_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.do_not_proceed_read === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Do Not Proceed Update"
                                variant="outlined"
                                sx={{
                                  borderColor: role.do_not_proceed_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.do_not_proceed_update === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Do Not Proceed Delete"
                                variant="outlined"
                                sx={{
                                  borderColor: role.do_not_proceed_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.do_not_proceed_delete === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                            </>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} alignItems="center">
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {isEditing[role.role_id] ? (
                            <>
                              <Chip 
                                label="Recently Deleted Read"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      recently_deleted_read: prev[role.role_id]?.recently_deleted_read === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.recently_deleted_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.recently_deleted_read === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Recently Deleted Update"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      recently_deleted_update: prev[role.role_id]?.recently_deleted_update === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.recently_deleted_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.recently_deleted_update === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <Chip
                                label="Recently Deleted Read"
                                variant="outlined"
                                sx={{
                                  borderColor: role.recently_deleted_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.recently_deleted_read === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Recently Deleted Update"
                                variant="outlined"
                                sx={{
                                  borderColor: role.recently_deleted_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.recently_deleted_update === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                            </>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} alignItems="center">
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {isEditing[role.role_id] ? (
                            <>
                              <Chip 
                                label="Org Settings Create"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      org_settings_create: prev[role.role_id]?.org_settings_create === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.org_settings_create === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.org_settings_create === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Org Settings Read"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      org_settings_read: prev[role.role_id]?.org_settings_read === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.org_settings_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.org_settings_read === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Org Settings Update"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      org_settings_update: prev[role.role_id]?.org_settings_update === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.org_settings_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.org_settings_update === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Org Settings Delete"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      org_settings_delete: prev[role.role_id]?.org_settings_delete === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.org_settings_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.org_settings_delete === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <Chip
                                label="Org Settings Create"
                                variant="outlined"
                                sx={{
                                  borderColor: role.org_settings_create === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.org_settings_create === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Org Settings Read"
                                variant="outlined"
                                sx={{
                                  borderColor: role.org_settings_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.org_settings_read === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Org Settings Update"
                                variant="outlined"
                                sx={{
                                  borderColor: role.org_settings_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.org_settings_update === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Org Settings Delete"
                                variant="outlined"
                                sx={{
                                  borderColor: role.org_settings_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.org_settings_delete === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                            </>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} alignItems="center">
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {isEditing[role.role_id] ? (
                            <>
                              <Chip 
                                label="Org Members Create"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      org_members_create: prev[role.role_id]?.org_members_create === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.org_members_create === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.org_members_create === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Org Members Read"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      org_members_read: prev[role.role_id]?.org_members_read === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.org_members_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.org_members_read === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Org Members Update"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      org_members_update: prev[role.role_id]?.org_members_update === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.org_members_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.org_members_update === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Org Members Delete"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      org_members_delete: prev[role.role_id]?.org_members_delete === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.org_members_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.org_members_delete === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <Chip
                                label="Org Members Create"
                                variant="outlined"
                                sx={{
                                  borderColor: role.org_members_create === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.org_members_create === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Org Members Read"
                                variant="outlined"
                                sx={{
                                  borderColor: role.org_members_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.org_members_read === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Org Members Update"
                                variant="outlined"
                                sx={{
                                  borderColor: role.org_members_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.org_members_update === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Org Members Delete"
                                variant="outlined"
                                sx={{
                                  borderColor: role.org_members_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.org_members_delete === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                            </>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} alignItems="center">
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {isEditing[role.role_id] ? (
                            <>
                              <Chip 
                                label="Org Roles Create"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      org_roles_create: prev[role.role_id]?.org_roles_create === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.org_roles_create === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.org_roles_create === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Org Roles Read"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      org_roles_read: prev[role.role_id]?.org_roles_read === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.org_roles_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.org_roles_read === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Org Roles Update"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      org_roles_update: prev[role.role_id]?.org_roles_update === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.org_roles_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.org_roles_update === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Org Roles Delete"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      org_roles_delete: prev[role.role_id]?.org_roles_delete === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.org_roles_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.org_roles_delete === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <Chip
                                label="Org Roles Create"
                                variant="outlined"
                                sx={{
                                  borderColor: role.org_roles_create === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.org_roles_create === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Org Roles Read"
                                variant="outlined"
                                sx={{
                                  borderColor: role.org_roles_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.org_roles_read === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Org Roles Update"
                                variant="outlined"
                                sx={{
                                  borderColor: role.org_roles_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.org_roles_update === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Org Roles Delete"
                                variant="outlined"
                                sx={{
                                  borderColor: role.org_roles_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.org_roles_delete === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                            </>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} alignItems="center">
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {isEditing[role.role_id] ? (
                            <>
                              <Chip 
                                label="Product Create"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      product_management_create: prev[role.role_id]?.product_management_create === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.product_management_create === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.product_management_create === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Product Read"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      product_management_read: prev[role.role_id]?.product_management_read === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.product_management_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.product_management_read === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Product Update"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      product_management_update: prev[role.role_id]?.product_management_update === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.product_management_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.product_management_update === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Product Delete"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      product_management_delete: prev[role.role_id]?.product_management_delete === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.product_management_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.product_management_delete === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <Chip
                                label="Product Create"
                                variant="outlined"
                                sx={{
                                  borderColor: role.product_management_create === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.product_management_create === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Product Read"
                                variant="outlined"
                                sx={{
                                  borderColor: role.product_management_read === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.product_management_read === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Product Update"
                                variant="outlined"
                                sx={{
                                  borderColor: role.product_management_update === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.product_management_update === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Product Delete"
                                variant="outlined"
                                sx={{
                                  borderColor: role.product_management_delete === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.product_management_delete === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                            </>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} alignItems="center">
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {isEditing[role.role_id] ? (
                            <>
                              <Chip 
                                label="Tools"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      tools: prev[role.role_id]?.tools === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.tools === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.tools === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                              <Chip 
                                label="Messages"
                                variant="outlined"
                                onClick={() => {
                                  setEditedRoles(prev => ({
                                    ...prev,
                                    [role.role_id]: {
                                      ...prev[role.role_id],
                                      messages: prev[role.role_id]?.messages === 1 ? 0 : 1
                                    }
                                  }));
                                }}
                                sx={{
                                  borderColor: editedRoles[role.role_id]?.messages === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: editedRoles[role.role_id]?.messages === 1 ? '#e8f5e9' : 'transparent',
                                  cursor: 'pointer',
                                  boxShadow: isEditing[role.role_id] ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    opacity: 0.8,
                                    boxShadow: isEditing[role.role_id] ? '0 0 12px rgba(76, 175, 80, 0.8)' : '0 0 4px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <Chip
                                label="Tools"
                                variant="outlined"
                                sx={{
                                  borderColor: role.tools === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.tools === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                              <Chip
                                label="Messages"
                                variant="outlined"
                                sx={{
                                  borderColor: role.messages === 1 ? '#4caf50' : '#e0e0e0',
                                  backgroundColor: role.messages === 1 ? '#e8f5e9' : 'transparent'
                                }}
                              />
                            </>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Members
                    </Typography>
                    {role?.members?.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {role.members.map(m => (
                          <Chip 
                            key={m.id}
                            avatar={<Avatar>{m.email.charAt(0).toUpperCase()}</Avatar>}
                            label={m.email}
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No members in this role
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}
      {activeTab === 3 && (
       <Grid container spacing={2}>
        {membersData.map((member) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={member.id}>
            <Card sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" mb={1}>
                {member.photo_base64 ? (
                  <Avatar src={member.photo_base64} />
                ) : (
                  <Avatar>{member.first_name?.charAt(0)}{member.last_name?.charAt(0)}</Avatar>
                )}
                <Box ml={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {member.first_name} {member.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {member.email}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body1">Role: <b>{member.role_name}</b></Typography>
              <Button 
                variant="outlined" 
                onClick={() => handleResetPassword(member)}
                sx={{ ml: 2 }}
              >
                Reset Password
              </Button>
            </Card>
          </Grid>
        ))}
      </Grid>
      )}
      {activeTab === 4 && ( 
        <Box sx={{ height: 'calc(100vh - 128px)', overflowY: 'auto', pr: 2, pl: 2, backgroundColor: 'rgba(235, 235, 235, 1)' }}>
          {tabLoading.jobFlows ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Individual Customers
                  </Typography>
                  {customersData?.individualCustomers?.length > 0 ? (
                    customersData.individualCustomers.map((customer) => (
                      <Card key={customer.customer_id} sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1">
                            {customer.customer_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Phone: {customer.customer_number}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Email: {customer.customer_email}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Orders: {customer.orders || 0}
                          </Typography>
                        </CardContent>
                        <CardActions>
                          <Button size="small" onClick={() => handleEditCustomer(customer)}>Edit Customer</Button>
                        </CardActions>
                      </Card>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No individual customers found
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Company Customers
                  </Typography>
                  {customersData?.companies?.length > 0 ? (
                    customersData.companies.map((company) => (
                      <React.Fragment key={company.company_id}>
                        <Card sx={{ mb: 2 }}>
                          <CardContent>
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer'
                              }}
                              onClick={() => setExpandedCompany(expandedCompany === company.company_id ? null : company.company_id)}
                            >
                              <Typography variant="subtitle1">
                                {company.company_name}
                              </Typography>
                              <IconButton size="small">
                                {expandedCompany === company.company_id ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              Phone: {company.company_number}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Email: {company.company_email}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Website: {company.company_website}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Orders: {company.orders || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Sub-customers: {company.customers?.length || 0}
                            </Typography>
                            <CardActions>
                              <Button 
                                size="small" 
                                onClick={() => handleEditCompany(company)}
                              >
                                Edit Company
                              </Button>
                            </CardActions>
                            <Collapse in={expandedCompany === company.company_id}>
                              <Box sx={{ mt: 2, boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)' }}>
                                {company.customers?.map((customer) => (
                                  <Card key={customer.customer_id} sx={{ mb: 2 }}>
                                    <CardContent>
                                      <Typography variant="subtitle1">
                                        {customer.customer_name}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        Phone: {customer.customer_number}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        Email: {customer.customer_email}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        Orders: {customer.orders || 0}
                                      </Typography>
                                    </CardContent>
                                    <CardActions>
                                      <Button size="small" onClick={() => handleEditSubCustomer(customer)}>Edit Sub-Customer</Button>
                                    </CardActions>
                                  </Card>
                                ))}
                              </Box>
                            </Collapse>
                          </CardContent>
                        </Card>
                      </React.Fragment>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No company customers found
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      )}

      {activeTab === 5 && (
        <Box sx={{ height: 'calc(100vh - 128px)', overflowY: 'auto', pr: 2 }}>
          {tabLoading.jobFlows ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {jobFlowsData.map((jobFlow) => (
                <Grid item xs={12} key={jobFlow.id}>
                  <Card sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h5">
                        {jobFlow.flow_name}
                      </Typography>
                      {jobFlow.default_job_flow ? (
                        <Chip
                          label="Default"
                          size="small"
                          sx={{
                            backgroundColor: '#4caf50',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      ) : (
                        <Chip
                          label="Custom"
                          size="small"
                          sx={{
                            backgroundColor: '#fff',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      )}
                    </Box>
                    

                    <Box sx={{ mt: 2 }}>
                      {jobFlow.steps && jobFlow.steps.sort((a, b) => a.step_number - b.step_number).map((step) => (
                        <Box key={step.id} sx={{ 
                          p: 2, 
                          mt: 1, 
                          borderLeft: `4px solid ${step.step_color}`,
                          backgroundColor: '#f5f5f5'
                        }}>
                          <Typography variant="subtitle1">
                            {step.step_number}. {step.step_name}
                            <Box component="span" sx={{ ml: 1, display: 'inline-flex', gap: 1 }}>
                              <Chip 
                                label="Email" 
                                size="small"
                                sx={{ 
                                  backgroundColor: step.email_notification ? '#4caf50' : '#e0e0e0',
                                  color: step.email_notification ? 'white' : 'rgba(65, 65, 65, 0.87)'
                                }} 
                              />
                              <Chip 
                                label="Update" 
                                size="small"
                                sx={{ 
                                  backgroundColor: step.allow_update ? '#4caf50' : '#e0e0e0',
                                  color: step.allow_update ? 'white' : 'rgba(65, 65, 65, 0.87)'
                                }} 
                              />
                            </Box>
                          </Typography>
                        
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption">Allowed Roles:</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                              {step.allowed_roles_id && step.orgRoles ? (
                                step.allowed_roles_id.split(',').map(roleId => (
                                    <Chip 
                                        key={roleId} 
                                        label={step.orgRoles[roleId] || roleId} 
                                        style={{ marginRight: 4, marginBottom: 4 }}
                                    />
                                ))
                            ) : (
                                <Typography variant="caption">None</Typography>
                            )}
                            </Box>
                          </Box>
                          
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption">Forms:</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, backgroundColor: 'rgba(235, 235, 235, 1)' }}>
                              {step.forms.length === 0 ? (
                                  <Typography variant="body2" sx={{ color: 'rgba(65, 65, 65, 0.87)' }}>None</Typography>
                              ) : (
                                step.forms.map((form) => (
                                  <Box key={form.id} sx={{
                                    marginLeft: 2,
                                    marginTop: 0,
                                    padding: 1,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    backgroundColor: '#f9f9f9'
                                  }}>
                                    {form.form_definition.fields.map((field) => (
                                      <Box key={field.name} sx={{
                                        marginTop: 0,
                                        padding: 1,
                                        backgroundColor: 'white',
                                        borderRadius: 1,
                                        border: '1px solid #e0e0e0',
                                      }}>
                                        <Typography variant="body2">
                                          <strong>Label:</strong> {field.label}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Type:</strong> {field.type}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Options:</strong> {field.options ? field.options.join(', ') : 'None'}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                ))
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
      {activeTab === 6 && (
        <Box sx={{ height: 'calc(100vh - 128px)', overflowY: 'auto', pr: 2 }}>
          {tabLoading.jobFlows ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product Department</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productsData.map((department) => (
                    <React.Fragment key={department.product_department_id}>
                      <TableRow>
                        <TableCell>
                          <IconButton 
                            onClick={() => toggleExpandDepartment(department.product_department_id)}
                          >
                            {expandedDepartments.includes(department.product_department_id) ? 
                              <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          {department.product_department_name}
                        </TableCell>
                        <TableCell>
                          {department.is_enabled ? 'Enabled' : 'Disabled'}
                        </TableCell>
                        <TableCell>
                          <IconButton disabled onClick={() => handleEditDepartment(department)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton disabled onClick={() => handleDeleteDepartment(department.product_department_id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {expandedDepartments.includes(department.product_department_id) && (
                        <Box sx={{ pl: 4 }}>
                          {productCategories?.length > 0 ? (
                            productCategories
                            .filter(cat => cat.product_department_id === department.product_department_id)
                            .map(category => (
                              <Box key={category.product_category_id} sx={{ width: '100%', mb: 1 }}>
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    p: 1,
                                    backgroundColor: 'rgba(0,0,0,0.02)',
                                    borderRadius: 1,
                                    ml: 0
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <IconButton size="small" onClick={() => handleToggleCategory(category.product_category_id)}>
                                      {expandedCategories.includes(category.product_category_id) 
                                        ? <ExpandLess /> 
                                        : <ExpandMore />}
                                    </IconButton>
                                    <Typography variant="subtitle1" sx={{ flex: 1, ml: 1 }}>
                                      {category.product_category_name}
                                    </Typography>
                                    <Chip 
                                      label={category.is_enabled ? 'Active' : 'Disabled'} 
                                      color={category.is_enabled ? 'success' : 'error'} 
                                      size="small"
                                      sx={{ mr: 2 }}
                                    />
                                    <Box>
                                      <IconButton disabled size="small" onClick={() => handleEditCategory(category)}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton disabled size="small" onClick={() => handleDeleteCategory(category.product_category_id)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                </Box>
                                {expandedCategories.includes(category.product_category_id) && (
                                  <Box sx={{ pl: 4, pt: 1 }}>
                                    {categoryItems
                                      ?.filter(item => item.product_category_id === category.product_category_id)
                                      ?.map(item => (
                                        <Box key={item.product_item_id} sx={{ 
                                          display: 'flex', 
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          width: '100%',
                                          p: 1,
                                          backgroundColor: 'rgba(0,0,0,0.02)',
                                          borderRadius: 1,
                                          mb: 1
                                        }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <Typography variant="body1" sx={{ flex: 1, ml: 1 }}>
                                              {item.item_name}
                                            </Typography>
                                            <Typography variant="body1" sx={{ mr: 2 }}>
                                              ${item.item_price}
                                            </Typography>
                                            <Chip 
                                              label={item.is_enabled ? 'Active' : 'Disabled'} 
                                              color={item.is_enabled ? 'success' : 'error'} 
                                              size="small"
                                              sx={{ mr: 2 }}
                                            />
                                            <Box>
                                              <IconButton disabled size="small" onClick={() => handleEditItem(item)}>
                                                <EditIcon fontSize="small" />
                                              </IconButton>
                                              <IconButton disabled size="small" onClick={() => handleDeleteItem(item.product_item_id)}>
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            </Box>
                                          </Box>
                                        </Box>
                                      ))
                                    }
                                  </Box>
                                )}
                              </Box>
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
                              No categories found
                            </Typography>
                          )}
                        </Box>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
      {activeTab === 7 && ( 
        <Box sx={{ height: 'calc(100vh - 128px)', overflowY: 'auto', pl: 2, pr: 2 }}>
          {tabLoading.products ? (
            <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Box>
          ) : formsData?.length > 0 ? (
            <Box>
              {formsData.map((form) => (
                <Box key={form.form_id} sx={{ 
                  mb: 4, 
                  p: 3, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 2,
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.05)'
                }}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h7" sx={{ 
                        color: 'white', 
                        backgroundColor: form.step_color,
                        p: 1,
                        borderRadius: 2
                      }}>
                        {form.step_name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                          variant="contained" 
                          onClick={() => handleEditForm(form.form_id)}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="contained" 
                          color="error"
                          onClick={() => handleDeleteForm(form)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                    <Typography variant="h8" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {form.flow_name} - Step {form.step_number}
                    </Typography>
                  </Box>
                  {form.form_definition?.fields?.map((field) => (
                    <Box key={field.name} sx={{ 
                      mb: 2, 
                      p: 2, 
                      bgcolor: '#fafafa', 
                      borderRadius: 1,
                      border: '1px solid #f0f0f0'
                    }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                        {field.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Type: {field.type}
                      </Typography>
                      {field.options && (
                        <Typography variant="body2" color="text.secondary">
                          Options: {field.options.join(', ')}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No forms available
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {activeTab === 8 && (
        <Box sx={{ height: 'calc(100vh - 128px)', overflowY: 'auto', pr: 2 }}>
          {tabLoading.tags ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Organization Tags</Typography>
              {tagsData.map((tag, index) => (
                <TextField
                  key={tag.tag_index}
                  label={`Tag ${index + 1}`}
                  value={tag.tag_name}
                  onChange={(e) => handleTagChange(tag.tag_index, e.target.value)}
                  multiline
                  rows={1}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              ))}
              <Button 
                variant="contained" 
                onClick={handleUpdateTags}
                disabled={isUpdatingTags}
              >
                {isUpdatingTags ? <CircularProgress size={24} /> : 'Update Tags'}
              </Button>
            </Box>
          )}
        </Box>
      )}
      {activeTab === 9 && (
        <Box sx={{ height: 'calc(100vh - 128px)', overflowY: 'auto', pr: 2 }}>
          {tabLoading.sessions ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ 
                    backgroundColor: '#f5f5f5',
                    '& th': {
                      fontWeight: 'bold',
                      fontSize: '0.875rem'
                    }
                  }}>
                    <TableCell>Session ID</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>Started At</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessionsData.map((session) => (
                    <TableRow key={session.session_id}>
                      <TableCell>{session.session_id}</TableCell>
                      <TableCell>{session.email}</TableCell>
                      <TableCell>{session.entity}</TableCell>
                      <TableCell>{new Date(session.started_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <Button 
                          variant="contained" 
                          color="error"
                          onClick={() => handleKillSession(session.session_id)}
                        >
                          Kill Session
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
      {activeTab === 10 && (
        <div>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  backgroundColor: '#f5f5f5',
                  '& th': {
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }
                }}>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eventsData.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      {editingEvent === event.id ? (
                        <TextField 
                          value={editedEvent.title}
                          onChange={(e) => handleFieldChange('title', e.target.value)}
                        />
                      ) : event.title}
                    </TableCell>
                    <TableCell>{event.type}</TableCell>
                    <TableCell>
                      {editingEvent === event.id ? (
                        <TextField 
                          type="datetime-local"
                          value={editedEvent.start_datetime}
                          onChange={(e) => handleFieldChange('start_datetime', e.target.value)}
                        />
                      ) : new Date(event.start_datetime).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {editingEvent === event.id ? (
                        <TextField 
                          type="datetime-local"
                          value={editedEvent.end_datetime}
                          onChange={(e) => handleFieldChange('end_datetime', e.target.value)}
                        />
                      ) : new Date(event.end_datetime).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {editingEvent === event.id ? (
                        <TextField 
                          value={editedEvent.location || ''}
                          onChange={(e) => handleFieldChange('location', e.target.value)}
                        />
                      ) : event.location || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {editingEvent === event.id ? (
                        <TextField 
                          value={editedEvent.description || ''}
                          onChange={(e) => handleFieldChange('description', e.target.value)}
                        />
                      ) : event.description || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {editingEvent === event.id ? (
                        <IconButton onClick={() => handleUpdateEvent(event.id)}>
                          <SaveIcon />
                        </IconButton>
                      ) : (
                        <IconButton onClick={() => handleEditEvent(event)}>
                          <EditIcon />
                        </IconButton>
                      )}
                      <IconButton onClick={() => handleDeleteEvent(event.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)}>
        <DialogTitle sx={{color: 'black'}}>Reset Password for <b>{selectedMember?.first_name} {selectedMember?.last_name}</b></DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)}>Cancel</Button>
          <Button onClick={handlePasswordSubmit} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={formsDialogOpen} onClose={() => setFormsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: 'black' }}>
          {editingFormId ? 'Edit Form' : 'Create New Form'}
          <IconButton 
            aria-label="close" 
            onClick={() => setFormsDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #ddd', padding: 12, width: '20%' }}>Name</th>
                <th style={{ border: '1px solid #ddd', padding: 12, width: '20%' }}>Type</th>
                <th style={{ border: '1px solid #ddd', padding: 12, width: '40%' }}>Dropdown Options</th>
                <th style={{ border: '1px solid #ddd', padding: 12, width: '20%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {formFields.map((field, index) => (
                <tr key={index}>
                  <td style={{ border: '1px solid #ddd', padding: 12 }}>{field.label}</td>
                  <td style={{ border: '1px solid #ddd', padding: 12 }}>{field.type}</td>
                  <td style={{ border: '1px solid #ddd', padding: 12 }}>{field.options ? field.options.join(', ') : ''}</td>
                  <td style={{ border: '1px solid #ddd', padding: 12 }}>
                    <Button variant="contained" color="error" size="small" onClick={() => handleDeleteQuestion(index)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ border: '1px solid #ddd', padding: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Question Name"
                    value={newQuestionName}
                    onChange={(e) => setNewQuestionName(e.target.value)}
                  />
                </td>
                <td style={{ border: '1px solid #ddd', padding: 12 }}>
                  <select
                    value={newQuestionType}
                    onChange={(e) => setNewQuestionType(e.target.value)}
                    style={{ width: '100%', padding: 8 }}
                  >
                    <option value="boolean">boolean</option>
                    <option value="dropdown">dropdown</option>
                    <option value="text">text</option>
                    <option value="datetime">datetime</option>
                  </select>
                </td>
                <td style={{ border: '1px solid #ddd', padding: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Comma-separated options"
                    value={newDropdownOptions}
                    onChange={(e) => setNewDropdownOptions(e.target.value)}
                    disabled={newQuestionType !== 'dropdown'}
                  />
                </td>
                <td style={{ border: '1px solid #ddd', padding: 12 }}>
                  <Button variant="contained" onClick={handleAddQuestion}>
                    Add Question
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveForm} disabled={formFields.length === 0}>
            Save Form
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: 'black' }}>Confirm Form Deletion</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            To delete form of <b>{formToDelete?.step_name}</b>, please enter the form name below:
          </Typography>
          <TextField
            fullWidth
            value={confirmationName}
            onChange={(e) => setConfirmationName(e.target.value)}
            placeholder="Enter form name to confirm"
            multiline
            rows={1}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete}
            disabled={confirmationName !== formToDelete?.step_name}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle sx={{ color: 'black' }}>Edit {editType === 'company' ? 'Company' : editType === 'subcustomer' ? 'Sub Customer' : 'Customer'}</DialogTitle>        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={currentEditData?.name || ''}
            onChange={(e) => setCurrentEditData({...currentEditData, name: e.target.value})}
            required
            multiline
            rows={1}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={currentEditData?.email || ''}
            onChange={(e) => setCurrentEditData({...currentEditData, email: e.target.value})}
            required
            multiline
            rows={1}
          />
          <TextField
            margin="dense"
            label="Phone"
            fullWidth
            value={currentEditData?.phone || ''}
            onChange={(e) => setCurrentEditData({...currentEditData, phone: e.target.value})}
            required
            multiline
            rows={1}
          />
          {editType === 'company' && (
            <TextField
              margin="dense"
              label="Website"
              fullWidth
              value={currentEditData?.website || ''}
              onChange={(e) => setCurrentEditData({...currentEditData, website: e.target.value})}
              required
              multiline
              rows={1}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={async () => {
              // Validate fields first
              if (!currentEditData.name || !currentEditData.email || !currentEditData.phone || 
                  (editType === 'company' && !currentEditData.website)) {
                alert('All fields are required!');
                return;
              }
              
              // Call the handler with the edit data
              await handleEditSubmit(currentEditData, editType);
              setEditDialogOpen(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminOrganizationDetails;